import { tagColors } from '../support/tagColors'

/**
 * Tags picker: colored chips + a filtering search input over the existing
 * tags, with the option to create new tags (first dropdown row when the
 * search term matches no existing tag exactly). Keyboard: ArrowUp/ArrowDown
 * to move, Enter to pick, Escape to close. The dropdown flips upwards when
 * the viewport space below the input runs out.
 *
 * The selection is entangled with the form state (array of tag names) - the
 * same binding contract the previous tagify implementation used.
 *
 * INVARIANT: `value` is handed in already entangled, from the blade. It must
 * NOT be produced by calling `$wire.entangle()` in init(): entangle returns an
 * Alpine INTERCEPTOR, and Alpine resolves interceptors once, while it walks the
 * object the x-data expression returned - before init() runs. Assigning one
 * afterwards stores the raw marker, so reads see no tags and writes never reach
 * livewire. Same contract as resourcerer's own pills/flatpickr fields.
 */
export default ({
  value = null,
  whitelist = [],
  localeScoped = false,
  locales = [],
  currentLocale = null,
  whitelistByLocale = {},
  max = null,
}) => ({
  value,
  whitelist,
  localeScoped,
  locales,
  currentLocale,
  whitelistByLocale,
  max,
  search: '',
  open: false,
  dropUp: false,
  dropdownIsFixed: true,
  dropdownTop: 0,
  dropdownLeft: 0,
  dropdownWidth: 0,
  dropdownMaxHeight: 256,
  highlight: 0,
  filtered: [],
  // 1 when the create-row is shown, else 0. Kept as state so the dropdown rows do
  // not each re-run canCreate() -> selected() -> currentMap().
  createOffset: 0,
  // Same reason: the blade reads this several times per render, and atLimit() walks
  // selected(). Refreshed by recompute(), which every add/remove/locale switch ends
  // with - the guards themselves still call atLimit() directly.
  limitReached: false,

  init() {
    if (this.localeScoped) {
      // Only prime the whitelist here. Never normalize `value` in place: an
      // empty map written back before the user acts would clobber the tags the
      // record already has. Reads go through currentMap() instead.
      this.whitelist = this.whitelistByLocale[this.currentLocale] ?? []
    }

    this.recompute()
    this.$watch('search', () => {
      this.highlight = 0
      this.recompute()
      this.$nextTick?.(() => { if (this.open) this.updateDropdownPosition() })
    })
    this.$watch('value', () => {
      this.recompute()
      this.$nextTick?.(() => { if (this.open) this.updateDropdownPosition() })
    })
  },

  // Switch the visible language purely client-side (the widget is wire:ignore,
  // so its per-locale state survives the switch round-trip). Fired by the
  // resourcerer-translateable locale switcher.
  onLocaleSwitched(event) {
    const detail = event?.detail
    const locale = detail && typeof detail === 'object' ? detail.locale ?? detail[0] : detail

    // Ignore unknown locales: an extra map key would fail the array:<locales>
    // validation on save. Only switch to a locale this field actually manages.
    if (!this.localeScoped || !locale || !this.locales.includes(locale)) {
      return
    }

    this.currentLocale = locale
    this.whitelist = this.whitelistByLocale[locale] ?? []
    this.search = ''
    this.highlight = 0
    this.recompute()
    this.$nextTick?.(() => { if (this.open) this.updateDropdownPosition() })
  },

  // Read-only view of the entangled value as a { locale: [names] } object with
  // an array for every configured locale. Never writes back: a locale the form
  // state has no entry for reads as empty, and that emptiness must not be
  // persisted until a real user action calls setSelected().
  currentMap() {
    const source = this.value && typeof this.value === 'object' && !Array.isArray(this.value)
      ? this.value
      : {}
    const map = {}

    this.locales.forEach((locale) => {
      const list = source[locale]
      map[locale] = list && typeof list === 'object'
        ? Object.values(list).filter((entry) => typeof entry === 'string')
        : []
    })

    return map
  },

  selected() {
    if (this.localeScoped) {
      return this.currentMap()[this.currentLocale] ?? []
    }

    if (Array.isArray(this.value)) {
      return this.value
    }

    // PHP arrays with non-sequential keys serialize as objects; anything
    // non-string in there is not a tag
    if (this.value && typeof this.value === 'object') {
      return Object.values(this.value).filter((entry) => typeof entry === 'string')
    }

    return []
  },

  // Replace the current locale's selection, keeping the other locales intact.
  // Reassigning `value` is what pushes through the entangle setter into the
  // form state - mutating the array in place would not. Only ever called from a
  // real user action (add/remove).
  setSelected(names) {
    if (this.localeScoped) {
      this.value = { ...this.currentMap(), [this.currentLocale]: names }

      return
    }

    this.value = names
  },

  openList() {
    if (!this.open) {
      this._reposition = () => this.updateDropdownPosition()
      window.addEventListener('scroll', this._reposition, true)
      window.addEventListener('resize', this._reposition)
    }
    this.open = true
    this.updateDropdownPosition()
    // Alpine has not shown the teleported element on the first synchronous pass.
    this.$nextTick?.(() => { if (this.open) this.updateDropdownPosition() })
  },

  closeList() {
    this.open = false
    if (this._reposition) {
      window.removeEventListener('scroll', this._reposition, true)
      window.removeEventListener('resize', this._reposition)
      this._transitionContext?.removeEventListener('transitionend', this._reposition)
      this._transitionContext = null
      this._reposition = null
    }
  },

  destroy() {
    this.closeList()
  },

  dropdownOrigin(dropdown) {
    if (this.dropdownIsFixed) return null

    // offsetParent is null while x-show keeps the newly teleported popup hidden.
    // Find the same containing block in the ancestor chain before it becomes
    // visible, rather than briefly positioning viewport coordinates in a dialog.
    if (dropdown.offsetParent) return dropdown.offsetParent
    for (let parent = dropdown.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
      const style = window.getComputedStyle(parent)
      if ((style.position && style.position !== 'static')
        || (style.transform && style.transform !== 'none')
        || (style.filter && style.filter !== 'none')
        || (style.perspective && style.perspective !== 'none')) {
        return parent
      }
    }
    return null
  },

  // The body portal uses viewport-fixed coordinates. Dialog portals stay inside
  // the focus trap and use coordinates relative to their positioned ancestor.
  updateDropdownPosition() {
    const anchor = this.$refs?.anchor ?? this.$refs?.search
    if (!anchor || typeof anchor.getBoundingClientRect !== 'function') {
      return
    }

    const rect = anchor.getBoundingClientRect()
    const scroller = this.$root?.closest?.('#action-dialog .content')
    const visibleArea = scroller?.getBoundingClientRect()
    if (rect.bottom <= 0 || rect.top >= window.innerHeight
      || (visibleArea && (rect.bottom <= visibleArea.top || rect.top >= visibleArea.bottom))) {
      this.closeList()
      return
    }

    const dropdown = this.$refs?.dropdown
    this.dropdownIsFixed = !dropdown || dropdown.parentElement === document.body
    const origin = dropdown && this.dropdownOrigin(dropdown)
    const bounds = origin?.getBoundingClientRect()
    // The dialog is scaled during its entry transition. Convert viewport pixels
    // back to its unscaled CSS coordinates, including the popup's width/height.
    const scaleX = bounds?.width && origin.offsetWidth ? bounds.width / origin.offsetWidth : 1
    const scaleY = bounds?.height && origin.offsetHeight ? bounds.height / origin.offsetHeight : 1
    const desiredHeight = Math.min(256, dropdown?.scrollHeight
      ? dropdown.scrollHeight + 2
      : dropdown?.offsetHeight || 256)
    const spaceBelow = Math.max(0, window.innerHeight - 8 - rect.bottom - 4)
    const spaceAbove = Math.max(0, rect.top - 8 - 4)
    this.dropUp = spaceBelow < desiredHeight * scaleY && spaceAbove > spaceBelow
    this.dropdownMaxHeight = Math.min(256, (this.dropUp ? spaceAbove : spaceBelow) / scaleY)

    const height = Math.min(desiredHeight, this.dropdownMaxHeight) * scaleY
    const top = this.dropUp ? rect.top - 4 - height : rect.bottom + 4
    const width = Math.min(rect.width || 0, window.innerWidth - 16)
    const left = Math.max(8, Math.min(rect.left || 0, window.innerWidth - 8 - width))
    const baseTop = bounds?.top ?? (this.dropdownIsFixed ? 0 : -window.scrollY)
    const baseLeft = bounds?.left ?? (this.dropdownIsFixed ? 0 : -window.scrollX)
    this.dropdownTop = (top - baseTop) / scaleY - (origin?.clientTop ?? 0) + (origin?.scrollTop ?? 0)
    this.dropdownLeft = (left - baseLeft) / scaleX - (origin?.clientLeft ?? 0) + (origin?.scrollLeft ?? 0)
    this.dropdownWidth = width / scaleX

    // The action dialog scales during its entrance transition. Reposition when
    // it reaches its final transform so the overlay remains aligned.
    if (origin && origin !== this._transitionContext && this._reposition) {
      this._transitionContext?.removeEventListener('transitionend', this._reposition)
      origin.addEventListener('transitionend', this._reposition)
      this._transitionContext = origin
    }
  },

  // The ->max() count limit, mirrored from the server rule so the picker stops at
  // the limit instead of letting the save fail - the same thing the core Pills
  // field does. In locale-scoped mode selected() is the CURRENT locale's list and
  // the rule counts per locale, so both sides compare the same two numbers.
  //
  // NOT a replacement for the rule: removing this attribute in the browser only
  // gets you a rejected save.
  atLimit() {
    return this.max !== null && this.selected().length >= this.max
  },

  recompute() {
    const term = this.search.toLowerCase()
    // Resolve the selection once: in locale-scoped mode selected() rebuilds the
    // whole map, so calling it per whitelist entry would be O(n * locales).
    const names = this.selected()
    const selected = new Set(names)

    // At the limit nothing is addable, so offer nothing - a row that silently does
    // nothing on click is worse than no row. The blade says WHY (max_reached).
    // names, not the Set: the rule counts the raw list too.
    this.limitReached = this.max !== null && names.length >= this.max
    this.filtered = this.limitReached ? [] : this.whitelist.filter(
      (tag) => !selected.has(tag) && tag.toLowerCase().includes(term),
    )

    this.createOffset = this.canCreate() ? 1 : 0
  },

  // The create row is shown when a search term exists and matches no
  // existing tag exactly (case-insensitive) and is not already selected.
  canCreate() {
    const term = this.search.trim()

    if (term === '' || this.atLimit()) {
      return false
    }

    const lower = term.toLowerCase()

    return !this.whitelist.some((tag) => tag.toLowerCase() === lower)
      && !this.selected().some((tag) => tag.toLowerCase() === lower)
  },

  // Entries navigable by keyboard: the create row (when present) first,
  // then the filtered existing tags.
  optionCount() {
    return this.filtered.length + this.createOffset
  },

  chipStyle(tag) {
    const color = tagColors.bgColorFromString(tag)

    return `--tag-bg:${color}; --tag-text-color:${tagColors.textColorForBackground(color)}`
  },

  add(tag) {
    const name = (tag || '').trim()

    if (name === '' || this.selected().includes(name) || this.atLimit()) {
      return
    }

    this.setSelected([...this.selected(), name])
    this.search = ''
    this.highlight = 0
    this.markDirty()
    this.recompute()
  },

  remove(tag) {
    this.setSelected(this.selected().filter((selectedTag) => selectedTag !== tag))
    this.markDirty()
    this.recompute()
  },

  move(direction) {
    const count = this.optionCount()

    if (!count) {
      return
    }
    this.openList()
    this.highlight = (this.highlight + direction + count) % count
  },

  choose() {
    const createOffset = this.canCreate() ? 1 : 0

    if (createOffset && this.highlight === 0) {
      this.add(this.search)

      return
    }

    const tag = this.filtered[this.highlight - createOffset]

    if (tag) {
      this.add(tag)
    }
  },

  markDirty() {
    const checkbox = this.$root.closest('.form-element')?.querySelector('label input[type=checkbox]')

    if (checkbox && !checkbox.checked) {
      checkbox.click()
    }
  },
})
